import {Color} from '@benchling/nucleus-ui';
import {Box, Group, Loader, Text, createStyles} from '@mantine/core';
import React from 'react';

import LLMMarkdownSection from 'llm/markdown/LLMMarkdownSection';
import {getObjectTypeDisplayName} from 'omnibox/omniboxConstants';
import type {ResultType} from 'omnibox/omniboxTypes';
import BenchlingIcon, {type BenchlingIconName, isValidBenchlingIcon} from 'ui-library/icons/BenchlingIcon';
import APIUtil, {apiIdToIconName} from 'util/APIUtil';

export interface OmniboxActionItem {
  id: string;
  title: string;
  description?: string;
  // A second muted line below the subtitle, e.g. a content-match snippet.
  snippet?: string;
  group?: string;
  resultType?: ResultType | undefined;
  apiId?: string;
  entryApiId?: string;
  objectType?: string;
  modelType?: string;
  modifiedAt?: string | null;
  // Why the LLM considered this result relevant. Only populated when LLM ranking is enabled.
  relevanceReason?: string;
  closeOnTrigger?: boolean;
  onTrigger: () => void;
}

interface OmniboxActionProps {
  action: OmniboxActionItem;
  hovered: boolean;
  onTrigger: () => void;
}

// --- Icon + color mapping for object types ---

interface IconConfig {
  icon: BenchlingIconName;
  bgColor: string;
  color: string;
}

// Maps domain graph class names (returned by backend) to human-friendly display names.
// The backend hydrates objectType via model_type_name_or_unknown() which returns class names
// like "DnaSequence", while recently-viewed items use display names like "DNA Sequence".
// See also MODEL_TYPE_DISPLAY_NAMES in omniboxConstants.ts which maps ModelTypeConstants
// enum values (snake_case) to display names for API-ID-based resolution.
const DOMAIN_CLASS_TO_DISPLAY_NAME: Record<string, string> = {
  DnaSequence: 'DNA Sequence',
  RnaSequence: 'RNA Sequence',
  DnaOligo: 'DNA Oligo',
  RnaOligo: 'RNA Oligo',
  AaSequence: 'Protein',
  BasicFolderItem: 'Custom Entity',
  MatrixPlate: 'Plate',
  WellPlate: 'Plate',
  Process: 'Workflow',
  ProcessTask: 'Workflow Task',
  ProcessOutput: 'Workflow Output',
  StageEntry: 'Stage Entry',
  AssayRun: 'Run',
  DatasetTable: 'Dataset',
  FileTable: 'File',
  StudyTable: 'Study',
  AnalysisTemplateTable: 'Automation Template',
  TemplateCollection: 'Template Collection',
  RequestV2SubmissionTable: 'Request',
  SequenceAnalysis: 'DNA/RNA Alignment',
  ProteinAlignment: 'AA Alignment',
  AnalyticsDashboard: 'Dashboard',
  BulkAssembly: 'Assembly',
  WorksheetTable: 'Worksheet',
};

export function normalizeObjectType(objectType: string | undefined): string | undefined {
  if (!objectType) {
    return undefined;
  }
  return DOMAIN_CLASS_TO_DISPLAY_NAME[objectType] ?? objectType;
}

// Color categories by ModelTypeConstants string value.
// apiIdToIconName handles per-type icon resolution; this only determines the color treatment.
type ColorPair = Pick<IconConfig, 'bgColor' | 'color'>;
const BLUE: ColorPair = {bgColor: Color.BLUE_100, color: Color.BLUE_350};
const PURPLE: ColorPair = {bgColor: Color.GRAPE_100, color: Color.GRAPE_500};
const GREEN: ColorPair = {bgColor: Color.GREEN_100, color: Color.GREEN_500};
const ORANGE: ColorPair = {bgColor: Color.ORANGE_100, color: Color.ORANGE_350};
const ANALYSIS: ColorPair = {bgColor: Color.ORANGE_150, color: Color.ORANGE_350};
const GRAY: ColorPair = {bgColor: Color.GRAY_100, color: Color.GRAY_700};
const NUCLEOTIDE: ColorPair = {bgColor: Color.GREEN_150, color: Color.GREEN_400};

const MODEL_TYPE_TO_COLOR: Record<string, Pick<IconConfig, 'bgColor' | 'color'>> = {
  // Entries & notebooks
  entry: BLUE,
  stage_entry: BLUE,
  protocol: BLUE,
  template: BLUE,
  template_collection: BLUE,
  subtemplate: BLUE,
  procedure: BLUE,
  document: BLUE,
  // Biology entities — nucleotides
  sequence: NUCLEOTIDE,
  rna_sequence: NUCLEOTIDE,
  oligo: NUCLEOTIDE, // DNA_OLIGO
  rna_oligo: NUCLEOTIDE,
  // Biology entities — other
  protein: PURPLE,
  basic_folder_item: PURPLE,
  mixture: PURPLE,
  molecule: PURPLE,
  // Workflows
  process: GREEN,
  process_task: GREEN,
  process_output: GREEN,
  workflow: GREEN,
  // Inventory
  container: ORANGE,
  plate: ORANGE,
  box: ORANGE,
  location: ORANGE,
  // Organization
  source: BLUE,
  folder: BLUE,
  // Data & analytics
  dataset: ANALYSIS,
  analysis: ANALYSIS,
  analytics_dashboard: ANALYSIS,
  study: ANALYSIS,
  assay_run: ANALYSIS,
  pipeline_file: GRAY, // FILE
  sequence_analysis: ANALYSIS,
  protein_alignment: ANALYSIS,
  worksheet: ANALYSIS,
  worklist: ANALYSIS,
  bulk_assembly: ANALYSIS,
  // Requests
  assay_request: BLUE,
  request_v2_submission: BLUE,
  connection: BLUE,
};

function getColorForModelType(modelType: string | null): Pick<IconConfig, 'bgColor' | 'color'> {
  if (modelType && MODEL_TYPE_TO_COLOR.hasOwnProperty(modelType)) {
    return MODEL_TYPE_TO_COLOR[modelType];
  }
  return GRAY;
}

// Omnibox-specific icon overrides by model type. These take precedence over the app-wide
// apiIdToIconName resolution so search results match the omnibox designs.
const MODEL_TYPE_TO_ICON: Record<string, BenchlingIconName> = {
  entry: 'file-lines',
  analysis: 'chart-mixed',
  sequence: 'dna',
  rna_sequence: 'dna',
  oligo: 'dna',
  rna_oligo: 'dna',
};

function getIconForApiId(apiId: string | undefined, objectType: string | undefined): IconConfig {
  if (apiId) {
    const modelType = APIUtil.apiIdToMaybeType(apiId);
    if (modelType) {
      const icon = MODEL_TYPE_TO_ICON[modelType] ?? apiIdToIconName(apiId);
      return {icon, ...getColorForModelType(modelType)};
    }
  }
  // Fallback for items without a recognized apiId
  return {icon: 'search', ...GRAY};
}

function getIconForResultType(resultType: string | undefined): IconConfig {
  switch (resultType) {
    case 'help_docs_article':
    case 'help_docs_loading':
      return {icon: 'study', bgColor: Color.RED_100, color: Color.RED_500};
    case 'advanced_search':
      return {icon: 'search', ...GRAY};
    case 'content':
      return {icon: 'paperclip', bgColor: Color.GRAY_100, color: Color.GRAY_600};
    case 'ask_suggestion':
      return {icon: 'sparkle', bgColor: Color.BLUE_100, color: Color.BRAND_ROYAL};
    case 'assigned_task_status':
      return {icon: 'done', ...GREEN};
    default:
      return {icon: 'search', ...GRAY};
  }
}

const useStyles = createStyles((theme) => ({
  resultIcon: {
    width: 32,
    height: 32,
    gap: 10,
    borderRadius: 4,
    padding: theme.spacing.xs,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  resultIconInner: {
    fontSize: 20,
    width: 20,
    height: 20,
  },
  // The advanced-search escalation row uses a smaller glyph within the same square, per design.
  resultIconInnerSmall: {
    fontSize: 16,
    width: 16,
    height: 16,
  },
  directAnswerTitle: {
    fontSize: 14,
    fontWeight: 500,
  },
  resultTitle: {
    lineHeight: 1,
  },
  resultDescription: {
    lineHeight: '14px',
    color: theme.colors.gray[5],
  },
  resultSnippet: {
    lineHeight: '14px',
    color: theme.colors.gray[5],
    fontStyle: 'italic' as const,
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 16px',
  },
  emptyState: {
    padding: 16,
    textAlign: 'center' as const,
  },
  suggestionOverflow: {
    overflow: 'hidden',
  },
  rightLabel: {
    flexShrink: 0,
  },
  resultInner: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minHeight: 32,
    flex: 1,
    minWidth: 0,
  },
  resultContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
    flex: 1,
    overflow: 'hidden',
    minWidth: 0,
  },
  modifiedAt: {
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
  },
}));

function ResultIcon({iconConfig, small = false}: {iconConfig: IconConfig; small?: boolean}): JSX.Element {
  const {classes} = useStyles();
  return (
    <div
      className={classes.resultIcon}
      style={{backgroundColor: iconConfig.bgColor, color: iconConfig.color}}
      aria-hidden="true"
    >
      <BenchlingIcon
        icon={iconConfig.icon}
        className={small ? classes.resultIconInnerSmall : classes.resultIconInner}
      />
    </div>
  );
}

// --- Date formatting ---

const relativeFormatter = new Intl.RelativeTimeFormat('en', {numeric: 'auto', style: 'narrow'});

function formatRelativeDate(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffSeconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const diffMinutes = Math.round(diffSeconds / 60);
  const diffHours = Math.round(diffSeconds / 3600);
  const diffDays = Math.round(diffSeconds / 86400);

  if (Math.abs(diffMinutes) < 1) {
    return 'just now';
  }
  if (Math.abs(diffHours) < 1) {
    return relativeFormatter.format(diffMinutes, 'minute');
  }
  if (Math.abs(diffDays) < 1) {
    return relativeFormatter.format(diffHours, 'hour');
  }
  if (Math.abs(diffDays) < 7) {
    return relativeFormatter.format(diffDays, 'day');
  }
  const month = date.toLocaleDateString('en-US', {month: 'short'});
  const day = date.getDate();
  if (date.getFullYear() === now.getFullYear()) {
    return `${month} ${day}`;
  }
  return `${month} ${day}, ${date.getFullYear()}`;
}

function formatAbsoluteDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// --- Main component ---

function DirectAnswerTitle({markdown}: {markdown: string}): JSX.Element {
  const {classes} = useStyles();
  return (
    <div className={`${classes.directAnswerTitle} omnibox-answer-markdown`}>
      <LLMMarkdownSection markdown={markdown} />
    </div>
  );
}

export default function OmniboxAction({action, hovered, onTrigger}: OmniboxActionProps): JSX.Element {
  const {classes} = useStyles();
  const resultType = action.resultType;
  const objectType = action.objectType;

  // Direct answer gets special full-width rendering
  if (resultType === 'direct_answer') {
    return (
      <Box
        data-hovered={hovered || undefined}
        onClick={onTrigger}
        sx={(theme) => ({
          display: 'block',
          width: '100%',
          padding: '10px 16px',
          borderRadius: theme.radius.sm,
          cursor: 'pointer',
          transition: 'background-color 100ms ease',
          backgroundColor: theme.colors.gray[0],
          ...(hovered ? {backgroundColor: theme.colors.gray[1]} : {}),
          '&:hover': {backgroundColor: theme.colors.gray[1]},
          '& .omnibox-answer-markdown *': {margin: '0 !important', padding: 0},
        })}
      >
        <DirectAnswerTitle markdown={action.title} />
        {action.description && (
          <Text color="dimmed" size="xs" mt={4}>
            {action.description}
          </Text>
        )}
      </Box>
    );
  }

  // Loading states
  if (resultType === 'filtered_recent_loading' || resultType === 'help_docs_loading') {
    const iconConfig = getIconForResultType(resultType);
    return (
      <div className={classes.loadingRow}>
        <ResultIcon iconConfig={iconConfig} />
        <Group noWrap={true} spacing={8} sx={{flex: 1}}>
          <Loader size="xs" />
          <Text size="sm" weight={500} color="dimmed" lineClamp={1} sx={{flex: 1}}>
            {action.title}
          </Text>
        </Group>
      </div>
    );
  }

  // Empty state
  if (resultType === 'filtered_recent_empty') {
    return (
      <div className={classes.emptyState}>
        <Text size="sm" color="dimmed">
          {action.title}
        </Text>
      </div>
    );
  }

  // Typeahead suggestions — compact rows with inline icons for types/schemas
  if (
    resultType === 'type_suggestion' ||
    resultType === 'schema_suggestion' ||
    resultType === 'project_suggestion' ||
    resultType === 'folder_suggestion' ||
    resultType === 'user_suggestion'
  ) {
    const isSchema = resultType === 'schema_suggestion';
    const isTypeOrSchema = resultType === 'type_suggestion' || isSchema;
    const rightLabel =
      resultType === 'type_suggestion'
        ? 'Type'
        : isSchema
          ? 'Schema'
          : resultType === 'project_suggestion'
            ? 'Project'
            : resultType === 'folder_suggestion'
              ? 'Folder'
              : undefined;

    // Resolve icon and color for type/schema suggestions.
    // Icons are resolved via the BenchlingIcon alias system (IconAliases.ts) which already
    // maps ModelTypeConstants values to icon names — the same system search uses.
    const isProjectOrFolder = resultType === 'project_suggestion' || resultType === 'folder_suggestion';
    const modelType = action.modelType;
    let suggestionIcon: BenchlingIconName | undefined;
    if (isTypeOrSchema && modelType && isValidBenchlingIcon(modelType)) {
      suggestionIcon = modelType;
    } else if (isProjectOrFolder) {
      suggestionIcon = resultType === 'project_suggestion' ? 'source' : 'folder';
    }
    const iconColor = getColorForModelType(suggestionIcon ?? modelType ?? null);

    return (
      <Box
        data-hovered={hovered || undefined}
        onClick={onTrigger}
        sx={(theme) => ({
          display: 'block',
          width: '100%',
          padding: '8px 16px',
          borderRadius: theme.radius.sm,
          cursor: 'pointer',
          transition: 'background-color 100ms ease',
          ...(hovered ? {backgroundColor: theme.colors.gray[1]} : {}),
          '&:hover': {backgroundColor: theme.colors.gray[0]},
        })}
      >
        <Group noWrap={true} position="apart">
          <Group noWrap={true} spacing={10} className={classes.suggestionOverflow}>
            {suggestionIcon ? (
              <BenchlingIcon
                icon={suggestionIcon}
                style={{fontSize: 14, color: iconColor.color, opacity: 0.6, flexShrink: 0}}
                aria-hidden="true"
              />
            ) : (
              <span style={{display: 'inline-block', width: 14, flexShrink: 0}} />
            )}
            <div className={classes.suggestionOverflow}>
              <Text size="sm" weight={500} lineClamp={1}>
                {action.title}
              </Text>
              {action.description && (
                <Text
                  size="xs"
                  color="dimmed"
                  lineClamp={1}
                  sx={isProjectOrFolder || isSchema ? {fontSize: 11} : undefined}
                >
                  {action.description}
                </Text>
              )}
            </div>
          </Group>
          {rightLabel && (
            <Text size="xs" color="dimmed" className={classes.rightLabel}>
              {rightLabel}
            </Text>
          )}
          {resultType === 'user_suggestion' && <BenchlingIcon icon="person" aria-hidden="true" />}
        </Group>
      </Box>
    );
  }

  // Recent search rows — compact single-line rows matching the Figma design
  if (resultType === 'recent_search') {
    const hasOpenedItem = Boolean(action.apiId);
    const iconColor = Color.GRAY_600;
    return (
      <Box
        data-hovered={hovered || undefined}
        onClick={onTrigger}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          padding: '4px 16px',
          minHeight: 27.5,
          borderRadius: theme.radius.sm,
          cursor: 'pointer',
          transition: 'background-color 100ms ease',
          ...(hovered ? {backgroundColor: theme.colors.gray[1]} : {}),
          '&:hover': {backgroundColor: theme.colors.gray[0]},
          '&:hover[data-hovered]': {backgroundColor: theme.colors.gray[1]},
        })}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            flexShrink: 0,
          }}
        >
          <BenchlingIcon
            icon={hasOpenedItem ? getIconForApiId(action.apiId, objectType).icon : 'clock-outline'}
            style={{fontSize: 13, color: iconColor}}
            aria-hidden="true"
          />
        </span>
        <Text size="sm" weight={400} lineClamp={1} sx={{flex: 1}}>
          {action.title}
        </Text>
      </Box>
    );
  }

  // All other results — card-like rows with icon + title + subtitle. The advanced-search
  // escalation row also renders here: a grey magnifying-glass icon (see getIconForResultType)
  // plus its title, with no subtitle.
  const useApiIdIcon =
    resultType === 'search_result' ||
    resultType === 'recent_item' ||
    resultType === 'filtered_recent_item' ||
    (resultType === 'content' && action.apiId);
  const iconConfig = useApiIdIcon
    ? getIconForApiId(action.apiId, objectType)
    : getIconForResultType(resultType);

  // The backend sends the literal "unknown" when it can't resolve a result's type — e.g. legacy
  // types like Protocol whose api-id prefix isn't registered in the domain graph. Rather than
  // surface that sentinel, fall back to deriving the type from the api-id prefix, which the client
  // can resolve (the same prefix map drives the result icon).
  const normalizedObjectType = normalizeObjectType(objectType);
  const subtitle =
    (normalizedObjectType === 'unknown' ? undefined : normalizedObjectType) ||
    (action.apiId ? getObjectTypeDisplayName(action.apiId) : undefined) ||
    (resultType === 'content' ? (action.apiId ? 'Entry' : 'Attachment') : undefined);

  // Ask suggestions and help docs have no relevanceReason and reuse this line for `description`.
  const secondaryText = action.relevanceReason ?? action.description;
  const modifiedAt = action.modifiedAt;

  return (
    <Box
      data-hovered={hovered || undefined}
      onClick={onTrigger}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.sm,
        width: '100%',
        minHeight: 48,
        padding: `${theme.spacing.xxs} ${theme.spacing.lg}`,
        borderRadius: theme.radius.sm,
        cursor: 'pointer',
        transition: 'background-color 100ms ease',
        ...(hovered ? {backgroundColor: theme.colors.gray[1]} : {}),
        '&:hover': {backgroundColor: theme.colors.gray[0]},
        '&:hover[data-hovered]': {backgroundColor: theme.colors.gray[1]},
      })}
    >
      <div className={classes.resultInner}>
        <ResultIcon iconConfig={iconConfig} small={resultType === 'advanced_search'} />
        <div className={classes.resultContent}>
          <Text size="md" weight={600} lineClamp={1} className={classes.resultTitle}>
            {action.title}
          </Text>
          {(subtitle || secondaryText) && (
            <Text size="sm" lineClamp={1} className={classes.resultDescription}>
              {subtitle}
              {subtitle && secondaryText ? '  ·  ' : ''}
              {secondaryText}
            </Text>
          )}
          {action.snippet && (
            <Text size="sm" lineClamp={1} className={classes.resultSnippet}>
              {action.snippet}
            </Text>
          )}
        </div>
      </div>
      {modifiedAt && (
        <Text size="xs" color="dimmed" className={classes.modifiedAt} title={formatAbsoluteDate(modifiedAt)}>
          {formatRelativeDate(modifiedAt)}
        </Text>
      )}
    </Box>
  );
}
